import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import LanguageSwitcher from '../components/LanguageSwitcher';

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!username.trim() || !password.trim()) {
      Swal.fire({ icon: 'warning', title: t('login.emptyFields'), text: t('login.emptyFieldsMsg') });
      return;
    }

    setLoading(true);
    try {
      await login(username, password);
      await Swal.fire({ icon: 'success', title: t('login.welcome'), text: t('login.welcomeMsg'), timer: 1500, showConfirmButton: false });
      navigate('/');
    } catch (err) {
      if (err.response?.data?.code === 'PASSWORD_EXPIRED') {
        Swal.fire({ icon: 'warning', title: t('login.pwdExpired'), text: t('login.pwdExpiredMsg') });
        return;
      }
      Swal.fire({ icon: 'error', title: t('login.failed'), text: err.response?.data?.error || '' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div style={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }}>
        <LanguageSwitcher style={{ color: '#fff' }} />
      </div>
      {/* Left Panel */}
      <div className="login-left">
        <div className="login-left-grid" />
        <div className="login-brand">
          <div className="login-logo-mark">
            <img src={require('../assets/images/TMS logo stack vertical white-02.png')} alt="TMS Logo" style={{ height: 100 }} />
          </div>
          <div className="login-hero-text">
            <h1>
              {t('login.title')}
            </h1>
            <p>
              {t('login.subtitle')}
            </p>
            <div className="login-stats">
              <div className="login-stat-item">
                <div className="login-stat-num">&#9201;</div>
                <div className="login-stat-label">{t('login.stat1')}</div>
              </div>
              <div className="login-stat-item">
                <div className="login-stat-num">&#9989;</div>
                <div className="login-stat-label">{t('login.stat2')}</div>
              </div>
              <div className="login-stat-item">
                <div className="login-stat-num">&#128200;</div>
                <div className="login-stat-label">{t('login.stat3')}</div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="login-right">
        <div className="login-form-card">
          <div className="login-form-header">
            <h2>{t('login.heading')}</h2>
            <p>{t('login.subheading')}</p>
          </div>

          <form onSubmit={handleSubmit}>

            <div className="form-group">
              <label className="form-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                {t('login.username')}
              </label>
              <input
                className="form-input"
                type="text"
                placeholder={t('login.usernamePlaceholder')}
                value={username}
                onChange={e => setUsername(e.target.value)}
                autoComplete="username"
              />
            </div>

            <div className="form-group">
              <label className="form-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                {t('login.password')}
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  className="form-input"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('login.passwordPlaceholder')}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="current-password"
                  style={{ paddingRight: 40 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--text-muted)', padding: 4,
                    display: 'flex', alignItems: 'center',
                  }}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button className="btn-primary" type="submit" disabled={loading}>
              {loading ? t('login.verifying') : t('login.submit')}
            </button>
          </form>

          <p style={{ textAlign: 'center', marginTop: 32, fontSize: 12, color: 'var(--text-muted)' }}>
            {t('login.version')}
          </p>
        </div>
      </div>
    </div>
  );
}
